import React, { useState } from "react";
import {
  AttributeContainer,
  AttributeName,
  AttributeValue,
  Card,
  CardWrapper,
  CheklistWrapper,
  DeleteWrapper,
  EditButton,
  EditButtonContainer,
  NameContainer,
  Table,
  TableCell,
  TableContainer,
  TableRow,
  Title,
} from "./styles";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import {
  ReportProblem,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { IconButton, Skeleton } from "@mui/material";
import { getUserById } from "../../services/user/getUser";
import { putUserName } from "../../services/user/putUserName";
import { useToasts } from "react-toast-notifications";
import theme from "../../theme/theme";
import { postChangePWD } from "../../services/user/postChangePWD";
import { postValidatePWD } from "../../services/user/postValidatePWD";
import DeleteAccountModal from "../../pages/DeleteAccountModal/DeleteAccountModal";
import PasswordStrengthBar from "react-password-strength-bar";
import ReactPasswordChecklist from "react-password-checklist";

export const PersonalDataCard = () => {
  // ------------------- JWT -------------------
  const jwt = localStorage.getItem("jwt");
  const userId = localStorage.getItem("user_id");
  const { addToast } = useToasts();

  // ------------------- STATES -------------------
  const [editingName, setEditingName] = useState(false);
  const [editingPassword, setEditingPassword] = useState(false);
  const [showRightColumn, setShowRightColumn] = useState(true);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [userName, setUserName] = React.useState("");
  const [userLastName, setUserLastName] = React.useState("");
  const [userEmail, setUserEmail] = React.useState("");
  const [saveButtonDisabled, setSaveButtonDisabled] = React.useState(true); // Estado del botón "Guardar"
  const [userPwd, setUserPwd] = useState("");
  const [currentPwd, setCurrentPwd] = useState("");
  const [newUserPwd, setNewUserPwd] = useState("");
  const [confirmUserPwd, setConfirmUserPwd] = useState("");
  const [validatePWD, setValidatePWD] = useState(false);
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [loading, setLoading] = useState(false);

  // ------------------- API REQUEST -------------------
  const getUser = async () => {
    if (userId) {
      await getUserById(parseInt(userId), jwt!)
        .then((res: any) => {
          if (res && res.data) {
            setUserName(res.data.user.name);
            setUserLastName(res.data.user.lastname);
            setUserEmail(res.data.user.email);
            setUserPwd(res.data.user.password);
            setLoading(true);
          }
        })
        .catch((err: any) => {
          console.log(err, "err");
        });
    }
  };

  const changeUserName = async () => {
    await putUserName(jwt!, parseInt(userId!), userName, userLastName)
      .then((res: any) => {
        if (res && res.data) {
          addToast("Nombre actualizado correctamente", {
            appearance: "success",
            autoDismiss: true,
          });
        }
      })
      .catch((err: any) => {
        console.log(err, "err");
        addToast("Error al actualizar nombre", {
          appearance: "error",
          autoDismiss: true,
        });
      });
  };

  const handleEditNameClick = () => {
    setEditingName(true);
    setShowRightColumn(false);
  };

  const handleSaveNameClick = () => {
    setEditingName(false);
    setShowRightColumn(true);
    changeUserName();
    // getUser();
  };

  const handleCancelEditName = () => {
    getUser();
    setEditingName(false);
    setShowRightColumn(true);
  };

  const handleEditPasswordClick = () => {
    setEditingPassword(true);
    setShowRightColumn(false);
  };

  const handleCancelEditPassword = () => {
    getUser();
    setEditingPassword(false);
    setShowRightColumn(true);
    setCurrentPwd("");
    setNewUserPwd("");
    setConfirmUserPwd("");
  };

  const handleSavePasswordClick = async () => {
    const passwordValidationRegex = /^(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*.])/;
    const isStrongPassword = passwordValidationRegex.test(newUserPwd);
  
    try {
      // Check if the current password is valid
      const response = await postValidatePWD(jwt!, userEmail, currentPwd);
  
      if (response.data.is_correct) {
        if (!isStrongPassword) {
          addToast("La contraseña no es segura", {
            appearance: "error",
            autoDismiss: true,
            icon: <ReportProblem />,
          });
        } else if (newUserPwd.length < 8) {
          addToast("La contraseña debe tener al menos 8 caracteres", {
            appearance: "error",
            autoDismiss: true,
          });
        } else if (newUserPwd !== confirmUserPwd) {
          setPasswordsMatch(false);
          addToast("La confirmación de la nueva contraseña no coincide", {
            appearance: "error",
            autoDismiss: true,
          });
        } else {
          // Change the user's password if all conditions are met
          await postChangePWD(jwt!, userEmail, newUserPwd);
  
          addToast("Contraseña actualizada correctamente", {
            appearance: "success",
            autoDismiss: true,
          });
  
          setEditingPassword(false);
          setShowRightColumn(true);
          setCurrentPwd("");
          setNewUserPwd("");
          setConfirmUserPwd("");
        }
      } else {
        // If current password is not correct, display an error message
        addToast("La contraseña actual no coincide", {
          appearance: "error",
          autoDismiss: true,
        });
      }
    } catch (error) {
      console.log(error, "error");
      addToast("Error al actualizar contraseña", {
        appearance: "error",
        autoDismiss: true,
      });
    }
  };

  const handleShowCurrentPassword = () => {
    setShowCurrentPassword(!showCurrentPassword);
  };

  const handleShowNewPassword = () => {
    setShowNewPassword(!showNewPassword);
  };

  const handleShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // ------------------- USE EFFECT -------------------

  React.useEffect(() => {
    getUser();
  }, []);

  React.useEffect(() => {
    // Esta función se ejecutará cada vez que userName o userLastName cambien.
    const isSaveButtonDisabled = () => {
      return !userName.trim() || !userLastName.trim();
    };

    // Actualiza el estado del botón "Guardar".
    setSaveButtonDisabled(isSaveButtonDisabled());
  }, [userName, userLastName]);

  return (
    <Card>
      <CardWrapper>
        <Title>Datos personales</Title>
        <TableContainer>
          <Table>
            <tbody>
              <TableRow>
                <TableCell>
                  {editingName ? (
                    <>
                      <AttributeName>Nombre completo</AttributeName>
                      <NameContainer>
                        <TextField
                          label="Nombre (s)"
                          value={userName}
                          variant="outlined"
                          fullWidth
                          required
                          onChange={(e) => setUserName(e.target.value)}
                          sx={{ margin: "15px 0" }}
                          InputProps={{
                            style: {
                              borderRadius: "14px",
                            },
                          }}
                        />
                        <TextField
                          label="Apellido (s)"
                          value={userLastName}
                          variant="outlined"
                          fullWidth
                          required
                          onChange={(e) => setUserLastName(e.target.value)}
                          sx={{ margin: "15px 0" }}
                          InputProps={{
                            style: {
                              borderRadius: "14px",
                            },
                          }}
                        />
                      </NameContainer>
                      <EditButtonContainer>
                        <Button
                          variant="contained"
                          onClick={handleCancelEditName}
                          sx={{
                            borderRadius: "12px",
                            backgroundColor: theme.color.stone,
                            padding: "10px 10%",
                            marginRight: "15px",
                            color: "#FFF",
                            textTransform: "none",
                            marginTop: "10px",
                            marginBottom: "15px",
                            "&:hover": {
                              backgroundColor: theme.color.graphite,
                            },
                          }}
                        >
                          Cancelar
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handleSaveNameClick}
                          disabled={saveButtonDisabled}
                          sx={{
                            borderRadius: "12px",
                            backgroundColor: "#13314D",
                            padding: "10px 10%",
                            color: "#FFF",
                            textTransform: "none",
                            marginTop: "10px",
                            marginBottom: "15px",
                            "&:hover": {
                              backgroundColor: "#111E2F",
                            },
                          }}
                        >
                          Guardar
                        </Button>
                      </EditButtonContainer>
                    </>
                  ) : (
                    <AttributeContainer>
                      <AttributeName>Nombre completo</AttributeName>
                      <AttributeValue disabled={editingPassword}>
                        {loading ? (
                          `${userName} ${userLastName}`
                        ):(
                          <Skeleton variant="text" sx={{ fontSize: '20px', width: '60%' }} />
                        )}
                      </AttributeValue>
                    </AttributeContainer>
                  )}
                </TableCell>
                {showRightColumn && (
                  <TableCell>
                    {editingName ? (
                      <EditButtonContainer>
                        <Button
                          variant="contained"
                          onClick={handleSaveNameClick}
                          disabled={saveButtonDisabled} // Desactiva el botón si nombre o apellido están vacíos
                          sx={{
                            borderRadius: "12px",
                            backgroundColor: "#13314D",
                            padding: "10px 10%",
                            color: "#FFF",
                            textTransform: "none",
                            "&:hover": {
                              backgroundColor: "#111E2F",
                            },
                          }}
                        >
                          Guardar
                        </Button>
                      </EditButtonContainer>
                    ) : (
                      <EditButton onClick={handleEditNameClick}>
                        Editar
                      </EditButton>
                    )}
                  </TableCell>
                )}
              </TableRow>
              <TableRow>
                <TableCell>
                  <AttributeContainer>
                    <AttributeName>Email</AttributeName>
                    <AttributeValue disabled={editingPassword || editingName}>
                      {loading ? (
                          `${userEmail}`
                        ):(
                          <Skeleton variant="text" sx={{ fontSize: '20px', width: '50%' }} />
                        )}
                    </AttributeValue>
                  </AttributeContainer>
                </TableCell>
                {showRightColumn && <TableCell>{/* Empty cell */}</TableCell>}
              </TableRow>
              <TableRow>
                <TableCell>
                  {editingPassword ? (
                    <>
                      <AttributeName>Contraseña</AttributeName>
                      <TextField
                        label="Contraseña actual"
                        type={showCurrentPassword ? "text" : "password"}
                        variant="outlined"
                        value={currentPwd}
                        onChange={(e) => setCurrentPwd(e.target.value)}
                        fullWidth
                        required
                        sx={{ margin: "15px 0 0 0" }}
                        InputProps={{
                          style: {
                            borderRadius: "14px",
                          },
                          endAdornment: (
                            <IconButton
                              onClick={handleShowCurrentPassword}
                              sx={{
                                minWidth: 0,
                                borderRadius: "30px",
                                background: "transparent",
                              }}
                            >
                              {showCurrentPassword ? (
                                <Visibility style={{ color: "A9A9A9" }} />
                              ) : (
                                <VisibilityOff style={{ color: "A9A9A9" }} />
                              )}
                            </IconButton>
                          ),
                        }}
                      />
                      <TextField
                        label="Nueva contraseña"
                        type={showNewPassword ? "text" : "password"}
                        variant="outlined"
                        fullWidth
                        value={newUserPwd}
                        onChange={(e) => setNewUserPwd(e.target.value)}
                        required
                        sx={{ margin: "15px 0 15px 0" }}
                        InputProps={{
                          style: {
                            borderRadius: "14px",
                          },
                          endAdornment: (
                            <IconButton
                              onClick={handleShowNewPassword}
                              sx={{
                                minWidth: 0,
                                borderRadius: "30px",
                                background: "transparent",
                              }}
                            >
                              {showNewPassword ? (
                                <Visibility style={{ color: "A9A9A9" }} />
                              ) : (
                                <VisibilityOff style={{ color: "A9A9A9" }} />
                              )}
                            </IconButton>
                          ),
                        }}
                      />
                      <PasswordStrengthBar
                        password={newUserPwd}
                        scoreWords={[
                          "Muy débil",
                          "Débil",
                          "Okay",
                          "Buena",
                          "Fuerte",
                        ]}
                        shortScoreWord={"Muy corta"}
                      />
                      <TextField
                        label="Confirmar contraseña"
                        type={showConfirmPassword ? "text" : "password"}
                        variant="outlined"
                        fullWidth
                        value={confirmUserPwd}
                        onChange={(e) => setConfirmUserPwd(e.target.value)}
                        required
                        error={!passwordsMatch}
                        helperText={
                          !passwordsMatch && "La confirmación no coincide"
                        }
                        sx={{ margin: "25px 0" }}
                        InputProps={{
                          style: {
                            borderRadius: "14px",
                          },
                          endAdornment: (
                            <IconButton
                              onClick={handleShowConfirmPassword}
                              sx={{
                                minWidth: 0,
                                borderRadius: "30px",
                                background: "transparent",
                              }}
                            >
                              {showConfirmPassword ? (
                                <Visibility style={{ color: "A9A9A9" }} />
                              ) : (
                                <VisibilityOff style={{ color: "A9A9A9" }} />
                              )}
                            </IconButton>
                          ),
                        }}
                      />
                      <CheklistWrapper>
                        <ReactPasswordChecklist
                          rules={[
                            "minLength",
                            "specialChar",
                            "number",
                            "capital",
                            "match",
                          ]}
                          minLength={8}
                          value={newUserPwd}
                          valueAgain={confirmUserPwd}
                          messages={{
                            minLength:
                              "La contraseña tiene más de 8 caracteres.",
                            specialChar:
                              "La contraseña tiene al menos un caracter especial.",
                            number: "La contraseña tiene al menos un número.",
                            capital:
                              "La contraseña tiene al menos una letra mayúscula.",
                            match: "Las contraseñas coinciden.",
                          }}
                        />
                      </CheklistWrapper>

                      <EditButtonContainer>
                        <Button
                          variant="contained"
                          onClick={handleCancelEditPassword}
                          sx={{
                            borderRadius: "12px",
                            backgroundColor: theme.color.stone,
                            padding: "10px 10%",
                            marginRight: "15px",
                            color: "#FFF",
                            textTransform: "none",
                            marginTop: "10px",
                            marginBottom: "15px",
                            "&:hover": {
                              backgroundColor: theme.color.graphite,
                            },
                          }}
                        >
                          Cancelar
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handleSavePasswordClick}
                          sx={{
                            borderRadius: "12px",
                            backgroundColor: "#13314D",
                            padding: "10px 10%",
                            color: "#FFF",
                            textTransform: "none",
                            marginTop: "10px",
                            marginBottom: "15px",
                            "&:hover": {
                              backgroundColor: "#111E2F",
                            },
                          }}
                        >
                          Guardar
                        </Button>
                      </EditButtonContainer>
                    </>
                  ) : (
                    <AttributeContainer>
                      <AttributeName>Contraseña</AttributeName>
                      <AttributeValue disabled={editingName}>
                        {loading ? (
                          `••••••••••`
                        ):(
                          <Skeleton variant="text" sx={{ fontSize: '20px', width: '55%' }} />
                        )}
                      </AttributeValue>
                    </AttributeContainer>
                  )}
                </TableCell>
                {showRightColumn && (
                  <TableCell>
                    {editingPassword ? (
                      <EditButtonContainer>
                        <Button
                          variant="contained"
                          onClick={handleSavePasswordClick}
                        >
                          Guardar
                        </Button>
                      </EditButtonContainer>
                    ) : (
                      <EditButton onClick={handleEditPasswordClick}>
                        Editar
                      </EditButton>
                    )}
                  </TableCell>
                )}
              </TableRow>
            </tbody>
          </Table>
        </TableContainer>
        <DeleteWrapper>
          {userId && <DeleteAccountModal userId={parseInt(userId)} disabled={editingPassword || editingName}/>}
        </DeleteWrapper>
      </CardWrapper>
    </Card>
  );
};
